import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import Places from 'constants/places'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import qs from 'qs'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useInView } from 'react-intersection-observer'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import Group from 'components/utils/Group'
import { OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'

const layoutContainerPadding = variables.gutterWidth / 2

const FilterChips = styled.div`
  display: flex;
  grid-row: 2;
  gap: ${rem(4)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /*
    negative margin left/right lets us 'overflow' the layout container to show more items are scrollable
    negiatve margin + padding top/bottom give pixel calculations space to not accidently hide chip borders
  */
  padding: ${rem(4)} ${rem(layoutContainerPadding)} ${rem(4)} ${rem(layoutContainerPadding)};
  margin: -${rem(4)} -${rem(layoutContainerPadding)} -${rem(4)} -${rem(layoutContainerPadding)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    padding: 0;
    margin: 0;
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: initial;
    }
  }
`

interface Props {
  filters?: App.OfferListFilters;
  places?: Array<App.Place>;
  title: React.ReactNode;
  category: string;
  sortType?: App.OfferListSortByOptions;
}

function TourPageSearchCarousel(props: Props) {
  const { places, category, sortType, title, filters } = props
  const [selectedPlace, setSelectedPlace] = useState(Places.Anywhere)

  const offerList = useOfferList(filters, { disabled: !filters })
  const offerIdsToExclude = useMemo(() => {
    if (selectedPlace.id === Places.Anywhere.id) {
      return take(offerList.offerIds, 5)
    }
    return EmptyArray
  }, [selectedPlace, offerList])
  const tourFacets = useTourFacetLocationCategory({ category, places })

  const searchUrl = useMemo(() => {
    const query = qs.stringify({
      destinationId: selectedPlace.id,
      destinationName: selectedPlace.name.split(',', 1)[0],
      categories: [category],
    }, { arrayFormat: 'comma' })
    const queryUrl = decodeURI(query).replace(/\s/g, '+')
    return `/search/tours?${queryUrl}`
  }, [selectedPlace, category])

  const onEvent = useContext(OfferListEventsContext)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })

  useEffect(() => {
    if (inView) {
      const params = { category }
      onEvent(OfferListEvents.impression, params)
    }
  }, [inView, category, onEvent])

  const locations = useMemo(() => {
    let locationsToDisplay: Array<any> = []
    if (places === undefined) {
      locationsToDisplay = Array.from(tourFacets.locations.values())
    } else if (places.length > 0) {
      locationsToDisplay = places.filter(place => tourFacets.locations.has(place.id))
    }
    return take([Places.Anywhere, ...locationsToDisplay], 5)
    // eslint-disable-next-line
  }, [places, tourFacets.locations])

  const filtersByCategory: App.OfferListFilters = useMemo(() => ({
    destinationId: selectedPlace.id,
    offerTypes: [OFFER_TYPE_TOUR_V2],
    categories: [category],
    sortBy: sortType,
    limit: 8,
  }), [selectedPlace, category, sortType])

  const tourIds = useOfferList(filtersByCategory, { excludedIds: offerIdsToExclude })

  if (!tourIds.fetching && tourIds.offerIds.length === 0) {
    return null
  }

  return (
    <Group direction="vertical" gap={20} ref={inViewRef}>
      <LayoutContainer>
        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <Heading variant="heading2">{title}</Heading>
            <TextButton
              kind="tertiary"
              to={searchUrl}
              variant="dark"
            >
              View all
            </TextButton>
          </Group>
          {locations.length > 1 && <FilterChips>
            {locations.map(place => <FilterChip
              key={place.id}
              size="medium"
              selected={selectedPlace.id === place.id}
              onClick={() => setSelectedPlace(place)}
            >
              {place === Places.Anywhere ? 'Around the world' : place.name}
            </FilterChip>,
            )}
          </FilterChips>}
        </Group>
      </LayoutContainer>
      <OfferListCarousel
        overrideOfferList={tourIds}
      />
    </Group>
  )
}

export default TourPageSearchCarousel
